import { SW_NOTIFICACIONES } from "@/components/service-worker-register";

/**
 * Web push en ESTE navegador: registrar el service worker de notificaciones, pedir permiso y
 * suscribirse con la llave pública VAPID del servidor. Requiere HTTPS (o localhost).
 */

export type EstadoPushNavegador = "no-soportado" | "bloqueado" | "inactivo" | "activo";

export function pushSoportado(): boolean {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

async function registro(): Promise<ServiceWorkerRegistration | undefined> {
  return navigator.serviceWorker.getRegistration(SW_NOTIFICACIONES);
}

export async function suscripcionActual(): Promise<PushSubscription | null> {
  if (!pushSoportado()) return null;
  const reg = await registro();
  return reg ? reg.pushManager.getSubscription() : null;
}

export async function estadoPushNavegador(): Promise<EstadoPushNavegador> {
  if (!pushSoportado()) return "no-soportado";
  if (Notification.permission === "denied") return "bloqueado";
  return (await suscripcionActual()) ? "activo" : "inactivo";
}

/** Pide permiso y crea la suscripción. Devuelve los datos que el backend necesita para enviar. */
export async function activarPush(clavePublica: string): Promise<{ endpoint: string; p256dh: string; auth: string }> {
  if (!pushSoportado()) throw new Error("Este navegador no admite notificaciones push.");
  const permiso = await Notification.requestPermission();
  if (permiso !== "granted") throw new Error("No se concedió el permiso de notificaciones en el navegador.");

  await navigator.serviceWorker.register(SW_NOTIFICACIONES);
  const reg = await navigator.serviceWorker.ready;
  const existente = await reg.pushManager.getSubscription();
  const sub =
    existente ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlABytes(clavePublica) }));
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) throw new Error("El navegador no devolvió una suscripción válida.");
  return { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth };
}

/** Cancela la suscripción de este navegador; devuelve su endpoint para quitarla también del servidor. */
export async function desactivarPush(): Promise<string | null> {
  const sub = await suscripcionActual();
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  return endpoint;
}

/** Nombre corto del navegador y sistema (para reconocer el dispositivo en la lista). */
export function nombreNavegador(): string {
  const ua = navigator.userAgent;
  const navegador = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Navegador";
  const so = /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Mac OS X/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : "";
  return so ? `${navegador} en ${so}` : navegador;
}

export function base64UrlABytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const relleno = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + relleno).replace(/-/g, "+").replace(/_/g, "/");
  const binario = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binario.length));
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return bytes;
}
